import { batch, useComputed, useSignalEffect, type ReadonlySignal } from "@preact/signals";
import { For, Show, useSignalRef } from "@preact/signals/utils";

import { type Anchor, type System } from ".";
import { type LogData, type Vec3 } from "../src/data";
import { dist2D, dist3D, projectDistance } from "./math";
import useTheme from "./theme";

const theme = useTheme();

interface DataPanelProps {
	loggedData: ReadonlySignal<LogData | undefined>,
	system: System,
}

export function fmtNum(n: number | undefined) {
	if (n == undefined) {
		return <span class="missing"> - </span>;
	} else if (isNaN(n)) {
		return <span class="nan"> NaN </span>;
	} else {
		return n.toFixed(2);
	}
}

function LiveAnchorData({ a, pos }: { a: Anchor, pos: ReadonlySignal<Vec3> }) {
	const expected = useComputed(() => dist2D(a.x, a.y, pos.value.x, pos.value.y));
	const expectedFmt = useComputed(() => fmtNum(expected.value));
	const diffFmt = useComputed(() => fmtNum(expected.value - a.distance.value));

	return (
		<>
			<div class="td" style={{ color: a.stale.value ? theme.peach : theme.green }}>
				{a.distance}
			</div>
			<div class="td">
				{expectedFmt}
			</div>
			<div class="td">
				{diffFmt}
			</div>
		</>
	);
}

function LoggedAnchorData({ a, data }: { a: Anchor, data: ReadonlySignal<LogData> }) {
	const isStatic = useComputed(() => data.value.type == "static");

	const center = useComputed(() => data.value.center);
	const expectedLoS = useComputed(() => dist3D(a, center.value));
	const expected = useComputed(() => projectDistance(expectedLoS.value, a.z, center.value.z));

	const stats = useComputed(() => data.value.ranges[a.addr]);

	if (isStatic.value) {
		return (
			<>
				<div class="td">{fmtNum(expectedLoS.value)}</div>
				<div class="td">{fmtNum(expected.value)}</div>
				<div class="td">{fmtNum(stats.value?.avg)}</div>
				<div class="td">{fmtNum(stats.value?.min)}</div>
				<div class="td">{fmtNum(stats.value?.max)}</div>
				<div class="td">{fmtNum(stats.value?.variance)}</div>
				<div class="td">{stats.value?.errors}</div>
			</>
		);
	}

	return (
		<>
			<div class="td"></div>
			<div class="td"></div>
			<div class="td"></div>
			<div class="td"></div>
			<div class="td"></div>
			<div class="td"></div>
			<div class="td"></div>
		</>
	);
}

export default function DataPanel({ loggedData, system: sys }: DataPanelProps) {
	const isLive = useComputed(() => loggedData.value == undefined);

	const checkAllRef = useSignalRef<HTMLInputElement | null>(null);

	const allChecked = useComputed(() => {
		let all = true;
		sys.anchors.forEach((a) => {
			if (!a.visible.value) {
				all = false;
			}
		});
		return all;
	});

	const mixChecked = useComputed(() => {
		let first;
		let mixed = false;
		sys.anchors.forEach((a, i) => {
			if (i == 0) {
				first = a.visible.value;
			} else if (a.visible.value != first) {
				mixed = true;
			}
		});
		return mixed;
	});

	useSignalEffect(() => {
		if (checkAllRef.current) {
			checkAllRef.current.indeterminate = mixChecked.value;
			checkAllRef.current.checked = allChecked.value;
		}
	});

	const tableCols = useComputed(() => {
		const cols = [
			<input type="checkbox" ref={checkAllRef} onChange={(e) => {
				batch(() => {
					sys.anchors.forEach((a) => a.visible.value = e.currentTarget.checked);
				});
				if (checkAllRef.current) {
					checkAllRef.current.indeterminate = false;
				}
			}} />,
			<b>Anchor</b>,
			/* <div class="th"><b>Pos</b></div>, */
		];

		if (isLive.value) {
			cols.push(
				<b>Reported</b>,
				<b>Expected</b>,
				<b>Diff</b>,
			);
		} else {
			cols.push(
				<b>3D dist.</b>,
				<b>2D dist.</b>,
				<b>Mean</b>,
				<b>Min</b>,
				<b>Max</b>,
				<b>Var</b>,
				<b>Errors</b>,
			);
		}

		return cols;
	});

	return (
		<div
			class="table"
			style={{ gridTemplateColumns: `repeat(${tableCols.value.length}, auto) 1fr` }}
			onMouseOut={() => sys.anchorHovered.value = -1}
		>
			<label class="row">
				<For each={tableCols}>
					{(c) => <div class="th">{c}</div>}
				</For>
				<div class="th spacer"></div>
			</label>
			{sys.anchors.map((a) => (
				<label class="row" onMouseOver={() => sys.anchorHovered.value = a.addr}>
					<div class="td">
						<input type="checkbox" checked={a.visible} onChange={(e) => a.visible.value = e.currentTarget.checked} />
					</div>
					<div class="td"><b>A{a.addr}</b></div>

					<Show when={isLive} fallback={<LoggedAnchorData a={a} data={loggedData as ReadonlySignal<LogData>} />}>
						<LiveAnchorData a={a} pos={sys.pos} />
					</Show>
					<div class="td spacer"></div>
				</label>
			))}
		</div>
	);
}
